/**
 * Stripe webhook signature verification (HMAC SHA256).
 * Uses timing-safe comparison. No dependency on stripe SDK.
 * @packageDocumentation
 */
import { createHmac, timingSafeEqual } from "node:crypto";
/**
 * Verifies Stripe-Signature header against payload using webhook secret.
 * Expects format "t=timestamp,v1=signature" and computes HMAC SHA256 of "timestamp.payload".
 *
 * @param payload - Raw request body string (must be unchanged from Stripe)
 * @param signatureHeader - Value of Stripe-Signature header
 * @param secret - Stripe webhook signing secret (whsec_...)
 * @returns true if signature is valid
 */
export function verifyStripeWebhookSignature(payload, signatureHeader, secret) {
    if (!signatureHeader || !secret) {
        return false;
    }
    const parts = signatureHeader.split(",");
    let timestamp = null;
    let v1Signature = null;
    for (const part of parts) {
        const [key, value] = part.split("=");
        if (key?.trim() === "t")
            timestamp = value?.trim() ?? null;
        if (key?.trim() === "v1")
            v1Signature = value?.trim() ?? null;
    }
    if (!timestamp || !v1Signature) {
        return false;
    }
    const signedPayload = `${timestamp}.${payload}`;
    const expected = createHmac("sha256", secret)
        .update(signedPayload)
        .digest("hex");
    if (expected.length !== v1Signature.length) {
        return false;
    }
    try {
        return timingSafeEqual(Buffer.from(expected, "hex"), Buffer.from(v1Signature, "hex"));
    }
    catch {
        return false;
    }
}
